import type { ReactNode } from 'react';
import { ChevronDownIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface EventFormSectionProps {
    /** Unique within the page - becomes `${id}-header` / `${id}-panel`. */
    id: string;
    title: string;
    /** One-line, muted state summary shown only while collapsed (the fields
     *  themselves already say this once the section is open). */
    summary?: string;
    /** Shown on the header regardless of `open` - a folded section must not
     *  be able to hide an error from the author. */
    hasError?: boolean;
    open: boolean;
    onToggle: () => void;
    /** Disables the fields, never the header - a read-only viewer still folds. */
    disabled?: boolean;
    children: ReactNode;
}

// The one collapsible-section shell every EventForm group renders through -
// see AccessCardSubsection (components/access/primitives) for the sibling
// pattern this borrows its toggle-button shape from. That component's
// micro-label styling and lack of aria-controls/id fit an AccessCard body
// row, not a form section with its own summary chip, so this is a fresh
// component rather than a reuse of it.
//
// Stays inside EventForm's single existing card - a hairline `border-b`
// under the header plus a bit more vertical rhythm (see the fieldset's
// `gap` in EventForm.tsx) is the whole separation treatment; the header text
// is nudged up one notch from `SECTION_TITLE_CLASS`, not onto a new scale.
export function EventFormSection({
    id,
    title,
    summary,
    hasError = false,
    open,
    onToggle,
    disabled = false,
    children
}: EventFormSectionProps) {
    const headerId = `${id}-header`;
    const panelId = `${id}-panel`;

    return (
        <section className="flex flex-col">
            <button
                type="button"
                id={headerId}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
                className="flex items-center gap-2 text-left -mx-8 px-4 pt-3 pb-2.5 border-b border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
            >
                <ChevronDownIcon
                    aria-hidden="true"
                    className={`h-4 w-4 text-gray-400 shrink-0 transition-transform duration-150 ${
                        open ? '' : '-rotate-90'
                    }`}
                />
                <span className="text-[15px] font-bold tracking-[0.05em] text-gray-800 uppercase shrink-0">
                    {title}
                </span>
                {hasError && (
                    <span
                        className="flex items-center gap-1 text-red-600 shrink-0"
                        title="This section has an error"
                    >
                        <ExclamationCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="sr-only">Has an error</span>
                    </span>
                )}
                {!open && summary && (
                    <span className="ml-auto text-xs font-normal normal-case text-gray-400 truncate max-w-[60%]">
                        {summary}
                    </span>
                )}
            </button>
            {/* Hidden, not unmounted, so an editor inside keeps its state and
                focus across a fold. The class carries the display, not just the
                `hidden` attribute: a class rule beats the UA `[hidden]` rule, so
                a `flex` here would silently win and the panel would never hide. */}
            <div
                id={panelId}
                role="region"
                aria-labelledby={headerId}
                hidden={!open}
                className={open ? 'block pt-4 pb-2' : 'hidden'}
            >
                {/* The disabled fieldset lives here, not around the section:
                    `fieldset[disabled]` disables every descendant control, which
                    would take the header button with it and leave a read-only
                    viewer unable to open a collapsed section. */}
                <fieldset disabled={disabled} className="flex flex-col gap-4">
                    {children}
                </fieldset>
            </div>
        </section>
    );
}
